import React, { useEffect, useRef } from 'react';
import { Box, ButtonBase, IconButton, InputBase, MenuItem, Select, Typography } from '@mui/material';
import { Close, PersonOutline, Search } from '@mui/icons-material';
import { SourceStatuses } from '../tasks/types';
import { INK } from '../ui';
import { CoachOption, INBOX_SEGMENTS, InboxSegment, segmentSourceState } from './inboxModel';

const CONTROL_HEIGHT = 40;
const fieldSx = {
  height: CONTROL_HEIGHT,
  borderRadius: '10px',
  bgcolor: '#FFFFFF',
  border: '1px solid #D5DCE5',
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
  '&:hover': { borderColor: '#B8C2CF' },
  '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.16)' },
} as const;

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

/**
 * Search: one clear field with its own clear button. "/" jumps to it from
 * anywhere on the page and Escape empties it.
 */
const SearchField: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey && !isTyping(event.target)) {
        event.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <Box role="search" sx={{ ...fieldSx, display: 'flex', alignItems: 'center', gap: 1, pl: 1.25, pr: 0.5, flex: { xs: '1 1 100%', sm: '0 1 300px' } }}>
      <Search aria-hidden sx={{ fontSize: 20, color: INK.muted }} />
      <InputBase
        inputRef={input}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && value) {
            event.preventDefault();
            onChange('');
          }
        }}
        placeholder="Search clients"
        inputProps={{ 'aria-label': 'Search clients', type: 'search', spellCheck: false }}
        sx={{ flex: 1, fontSize: 14, color: INK.main, '& input::-webkit-search-cancel-button': { display: 'none' }, '& input::placeholder': { color: INK.muted, opacity: 1 } }}
      />
      {value ? (
        <IconButton aria-label="Clear search" size="small" onClick={() => { onChange(''); input.current?.focus(); }} sx={{ color: INK.muted }}>
          <Close fontSize="small" />
        </IconButton>
      ) : (
        <Box
          component="kbd"
          aria-hidden
          sx={{ display: { xs: 'none', md: 'inline-flex' }, alignItems: 'center', justifyContent: 'center', minWidth: 22, height: 22, mr: 0.75, borderRadius: '6px', border: `1px solid ${INK.hairline}`, bgcolor: '#F8FAFC', color: INK.muted, fontSize: 12, fontFamily: 'inherit' }}
        >
          /
        </Box>
      )}
    </Box>
  );
};

/** Lead-coach filter for managers and admins; each option shows how many clients it would leave. */
const CoachSelect: React.FC<{ options: CoachOption[]; total: number; value: string | null; onChange: (coach: string | null) => void }> = ({
  options,
  total,
  value,
  onChange,
}) => {
  const selected = options.find((option) => option.key === value);
  return (
    <Select
      value={value ?? ''}
      displayEmpty
      onChange={(event) => onChange(event.target.value ? String(event.target.value) : null)}
      inputProps={{ 'aria-label': 'Lead coach' }}
      renderValue={() => (
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonOutline aria-hidden sx={{ fontSize: 20, color: INK.muted }} />
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {selected ? selected.name : value ? 'Coach with nothing open' : 'All coaches'}
          </Box>
        </Box>
      )}
      MenuProps={{ PaperProps: { sx: { maxHeight: 360, mt: 0.5, borderRadius: '10px' } } }}
      sx={{
        ...fieldSx,
        flex: { xs: '1 1 100%', sm: '0 1 240px' },
        minWidth: 0,
        fontSize: 14,
        '& .MuiOutlinedInput-notchedOutline': { border: 0 },
        '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0, pl: 1.25 },
      }}
    >
      <MenuItem value="" sx={{ fontSize: 14 }}>
        <Box sx={{ flex: 1 }}>All coaches</Box>
        <Typography component="span" sx={{ fontSize: 13, color: INK.muted, ml: 2 }}>
          {total}
        </Typography>
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option.key} value={option.key} sx={{ fontSize: 14 }}>
          <Box sx={{ flex: 1 }}>{option.name}</Box>
          <Typography component="span" sx={{ fontSize: 13, color: INK.muted, ml: 2 }}>
            {option.count}
          </Typography>
        </MenuItem>
      ))}
    </Select>
  );
};

export const InboxToolbar: React.FC<{
  segment: InboxSegment;
  onSegment: (segment: InboxSegment) => void;
  segmentCounts: Record<InboxSegment, number>;
  sources: SourceStatuses;
  coachFilter?: { options: CoachOption[]; total: number; value: string | null; onChange: (coach: string | null) => void };
  search: string;
  onSearch: (value: string) => void;
}> = ({ segment, onSegment, segmentCounts, sources, coachFilter, search, onSearch }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
    <Box role="group" aria-label="Kind of work" sx={{ display: 'flex', p: 0.5, gap: 0.5, bgcolor: '#E7EBF0', borderRadius: '12px', flex: { xs: '1 1 100%', md: '0 0 auto' } }}>
      {INBOX_SEGMENTS.map(({ key, label }) => {
        const on = key === segment;
        const state = segmentSourceState(key, sources);
        return (
          <ButtonBase
            key={key}
            aria-pressed={on}
            onClick={() => onSegment(key)}
            sx={{
              flex: { xs: 1, md: '0 0 auto' },
              height: 36,
              px: 1.75,
              borderRadius: '9px',
              fontSize: 14,
              fontWeight: 600,
              color: on ? INK.main : INK.body,
              bgcolor: on ? '#FFFFFF' : 'transparent',
              boxShadow: on ? '0 1px 2px rgba(15,23,42,0.12)' : 'none',
              '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
            }}
          >
            {label}
            <Box component="span" sx={{ ml: 0.75, fontWeight: 500, color: INK.muted }}>
              {state === 'error' ? '-' : segmentCounts[key]}
              {state === 'loading' ? '…' : ''}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
    <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
    {coachFilter ? <CoachSelect {...coachFilter} /> : null}
    <SearchField value={search} onChange={onSearch} />
  </Box>
);
